// ============================================================
// FTA_EVENTS.js  —  EV Powertrain Simulator  (ISO 26262 / SOTIF / IEC 61025)
// EV Powertrain Simulator — 결함 수목 분석(FTA) 이벤트 상수
// 참조 표준: IEC 61025 (FTA 방법론), ISO 26262-9 §5 (ASIL 분해·PMHF),
//            ISO 26262-5 §9.4 (HW 아키텍처 메트릭)
//
// 이 파일이 포함하는 데이터:
//   HOUSE_EVENTS           — 외부 조건 이벤트 (운용 환경 모델, 고정 확률)
//   FTA_TOP_EVENTS         — 최상위 위험 사건(Top Event) 5종
//   FTA_INTERMEDIATE_EVENTS— 중간 사건(Intermediate Event) 및 게이트 구조
//
// 지원 게이트 타입 (IEC 61025 §4.4):
//   OR      — 자식 중 1개 이상 발생 시 출력
//   AND     — 모든 자식 동시 발생 시 출력 (CCF β 보정 적용)
//   INHIBIT — 주 이벤트 + 조건 이벤트(HE-xx) 동시 성립 시 출력
//   TRANSFER— 다른 Top Event 하위 트리 재사용 (transfer_to 필드)
// ============================================================

// ── House Events (외부 조건 이벤트) ───────────────────────────────────────────
// IEC 61025 §4.4.3 — 시스템 외부에서 설정되는 고정 확률 이벤트
// Inhibit 게이트의 조건 이벤트, 또는 운용 환경 모델링에 사용
/**
 * HOUSE_EVENTS — 운용 환경 외부 조건 이벤트
 * 출처: 국내 주행 통계 및 IEA 운행 데이터 기반 추정
 * 단위: prob [무차원] — 연간 운행 시간 대비 비율
 * 사용처: FTA_INTERMEDIATE_EVENTS INHIBIT 게이트의 조건 이벤트로 참조
 */
export const HOUSE_EVENTS = {
  "HE-01": { name:"저온 환경 (−20°C↓)",   nameEn:"Cold Ambient (below −20°C)",   prob:0.05,
             description:"연간 운행 시간 대비 극저온 환경 노출 비율 (한반도 기준 ~5%)" },
  "HE-02": { name:"고속 주행 (80km/h↑)",   nameEn:"High Speed (above 80 km/h)",   prob:0.30,
             description:"고속도로 주행 비율 (주행 통계 기반 추정)" },
  "HE-03": { name:"ADAS 모드 활성",         nameEn:"ADAS Mode Active",             prob:0.40,
             description:"Level 2 이상 자율주행 기능 활성 비율 (ADAS 장착 차량 기준)" },
};

// ── FTA Top Events ─────────────────────────────────────────────────────────────
// [Add-TE-gate] gate 필드 추가: 복수 중간 원인을 결합하는 게이트 타입 명시
//   기본값 "OR" — 이전에는 하드코딩이었으나 이제 buildFaultTree가 te.gate를 읽음
//   "AND"로 설정 시 모든 중간 원인이 동시에 발생해야 Top Event 성립
/**
 * FTA_TOP_EVENTS — FTA 최상위 위험 사건 목록
 * 출처: ISO 26262-3 HARA 기반 위험 식별 결과
 * 단위: target_pfh [/h] — ASIL D 목표 < 10⁻⁸/h, ASIL C < 10⁻⁷/h
 * 사용처: FMEASafetyTabContent.jsx FTA 트리 렌더링, PMHF 계산
 *
 * 필드 설명:
 *   gate               — 중간 원인 결합 게이트 타입 (OR/AND)
 *   intermediate_causes— 하위 중간 사건 ID 목록 (IE-xx)
 *   target_pfh         — ISO 26262 ASIL 기반 목표 고장 확률 [/h]
 *   prob_base          — 현재 설계 추정 고장 확률 [/h]
 */
export const FTA_TOP_EVENTS = [
  { id:"TE-01", name:"비의도 가속 (UA)",   nameEn:"Unintended Acceleration (UA)",
    gate:"OR", intermediate_causes:["IE-01","IE-02","IE-10"], target_pfh:1e-8, prob_base:2.5e-8 },
  { id:"TE-02", name:"제동 기능 상실",      nameEn:"Loss of Braking Function",
    gate:"OR", intermediate_causes:["IE-03","IE-04"],         target_pfh:1e-8, prob_base:1.8e-8 },
  { id:"TE-03", name:"열폭주 / 화재",       nameEn:"Thermal Runaway / Fire",
    gate:"OR", intermediate_causes:["IE-05","IE-06","IE-11"], target_pfh:1e-7, prob_base:3.0e-8 },
  { id:"TE-04", name:"고압 감전",           nameEn:"HV Electric Shock",
    gate:"OR", intermediate_causes:["IE-07"],                 target_pfh:1e-8, prob_base:5.0e-9 },
  { id:"TE-05", name:"조향 제어 상실",      nameEn:"Loss of Steering Control",
    gate:"OR", intermediate_causes:["IE-08","IE-09"],         target_pfh:1e-8, prob_base:1.2e-8 },
];

// ── FTA Intermediate Events ────────────────────────────────────────────────────
/**
 * FTA_INTERMEDIATE_EVENTS — FTA 중간 사건 및 게이트 구조
 * 출처: IEC 61025 §4 게이트 정의 기반 EV 파워트레인 위험 분석
 * 사용처: FMEASafetyTabContent.jsx buildFaultTree() 재귀 트리 전개
 *
 * 필드 설명:
 *   gate                — 게이트 타입 (OR/AND/INHIBIT/TRANSFER)
 *   basic_events        — 기본 사건 ID 목록 (FM-xx, HE-xx)
 *   intermediate_events — 하위 중간 사건 ID 목록 (IE-xx) — Level-3 트리에 사용
 *   ccf_beta            — AND 게이트 공통 원인 고장 β 계수 (ISO 26262-9 §5.4)
 *   transfer_to         — TRANSFER 게이트: 재사용할 Top Event ID
 *   prob_base           — 추정 발생 확률 [/h]
 */
//
// 지원 게이트 타입 (IEC 61025 §4.4):
//   "OR"      — 자식 중 하나 이상 발생 시 출력 (기본)
//   "AND"     — 모든 자식 동시 발생 시 출력; CCF β 보정 적용
//   "INHIBIT" — 주 이벤트 + 조건 이벤트(HE-xx) 동시 성립 시 출력
//               basic_events[0..n-1] = 주 이벤트, HE-xx = 조건 이벤트
//   "TRANSFER"— transfer_to: "TE-xx" 로 다른 Top Event 하위 트리를 재사용
//
// CCF 관련:
//   AND 게이트에 ccf_beta: 값 → ISO 26262-9 §5.4 명시적 β 지정
//   미지정 시 자식 ASIL 최고 레벨 기반 CCF_BETA_FTA 룩업 적용
export const FTA_INTERMEDIATE_EVENTS = {
  // ── TE-01: 비의도 가속 ──────────────────────────────────────────────────────
  "IE-01":{ name:"APS/토크 제어 오류",    nameEn:"APS / Torque Control Error",
            gate:"OR",  basic_events:["FM-01","FM-03","FM-04","FM-11"],         prob_base:1.5e-8 },
  "IE-02":{ name:"CAN 통신 지연·손실",    nameEn:"CAN Comm Delay / Loss",
            gate:"OR",  basic_events:["FM-02","FM-04","FM-28"],                  prob_base:8.0e-9 },
  // [Add-IE-10] ADAS/자율 토크 충돌 — INHIBIT 게이트 (신규)
  //   IEC 61025 §4.4.4 Inhibit: FM-26 단독으로는 발생 안 함
  //   HE-03(ADAS 모드 활성) 조건이 성립할 때만 출력
  //   P(IE-10) = P(FM-26) × P(HE-03) = 3e-9 × 0.40 = 1.2e-9
  "IE-10":{ name:"ADAS 토크 충돌 (조건부)",nameEn:"ADAS Torque Conflict (Conditional)",
            gate:"INHIBIT", basic_events:["FM-26","HE-03"],                      prob_base:1.2e-9 },

  // ── TE-02: 제동 기능 상실 ──────────────────────────────────────────────────
  // [Fix-IE03] AND→OR, 인버터 계열 고장으로 재구성 (이전 수정 유지)
  "IE-03":{ name:"회생제동 상실",          nameEn:"Regen Braking Loss",
            gate:"OR",  basic_events:["FM-08","FM-13","FM-14"],                  prob_base:8.4e-8 },
  // [Fix-IE04] SAS 제거, ESC·EPB 포함 (이전 수정 유지)
  "IE-04":{ name:"ABS·EPB 응답 실패",     nameEn:"ABS / EPB Response Failure",
            gate:"OR",  basic_events:["FM-15","FM-09","FM-19"],                  prob_base:4.3e-8 },

  // ── TE-03: 열폭주 / 화재 ───────────────────────────────────────────────────
  // 3계층 트리 구조: IE-05 → IE-05A / IE-05B (Level-3 서브 IE)
  // [Fix-IE05-struct] intermediate_events 필드 도입:
  //   basic_events  : FM-xx (기본 사건) 만 수록
  //   intermediate_events: IE-xx (중간 사건) 만 수록 → buildNode에서 IE 분기 처리
  //   기존 혼재 방식(basic_events에 IE-05A/B 포함)은 FM 분기와 구조 충돌 가능성 있음
  "IE-05A":{ name:"배터리 열 감지 실패",  nameEn:"Battery Thermal Sensing Failure",
             gate:"OR",
             basic_events:["FM-07","FM-17","FM-24"],   // FM-17:Li-plating, FM-24:셀 불균형
             prob_base:1.5e-8 },
  "IE-05B":{ name:"냉각 시스템 실패",     nameEn:"Cooling System Failure",
             gate:"OR",
             basic_events:["FM-11","FM-12"],            // FM-11:냉각펌프, FM-12:SOC오차→과방전
             prob_base:2.4e-7 },
  // IE-05: OR 게이트 — IE-05A / IE-05B (중간 사건) + FM-06(저전압 과방전) 포함
  //   [Fix-IE05-struct] intermediate_events 필드로 하위 IE 명시 분리
  //   buildFaultTree.buildNode()는 IE-xx를 FTA_INTERMEDIATE_EVENTS에서 조회하므로
  //   basic_events에 IE ID를 넣어도 동작하지만, 구조 명확성을 위해 별도 필드로 분리
  //   P(IE-05) ≈ P(IE-05A OR IE-05B OR FM-06) ≈ 1 − (1−1.5e-8)(1−2.4e-7)(1−4e-8) ≈ 2.9e-7
  "IE-05":{ name:"배터리 열 제어 실패",   nameEn:"Battery Thermal Control Failure",
            gate:"OR",
            intermediate_events:["IE-05A","IE-05B"],   // 하위 중간 사건 (Level-3)
            basic_events:["FM-06"],                     // 저전압 과방전 → 열 스트레스
            prob_base:2.9e-7 },
  "IE-06":{ name:"접촉기 고착·오개방",    nameEn:"Contactor Weld / Misopen",
            gate:"OR",  basic_events:["FM-05","FM-14"],                          prob_base:5.0e-9 },
  // [Add-IE-11] 냉각수 동결 — INHIBIT 게이트 (신규)
  //   냉각수 동결(FM-27)은 저온 환경(HE-01) 조건에서만 발생
  //   P(IE-11) = P(FM-27) × P(HE-01) = 5e-9 × 0.05 = 2.5e-10
  "IE-11":{ name:"냉각수 동결 고장 (조건부)",nameEn:"Coolant Freeze Fault (Conditional)",
            gate:"INHIBIT", basic_events:["FM-27","HE-01"],                      prob_base:2.5e-10 },

  // ── TE-04: 고압 감전 ───────────────────────────────────────────────────────
  // [Fix-7 유지] gate OR, FM-25(하네스 단선) 추가
  "IE-07":{ name:"절연 파괴·IMD 미감지",  nameEn:"Insulation Breakdown / IMD Fail",
            gate:"OR",  basic_events:["FM-20","FM-05","FM-14","FM-21","FM-25"],  prob_base:6.5e-9 },
            // FM-21(절연재 열화), FM-25(하네스 단선) — 고압 감전의 추가 경로

  // ── TE-05: 조향 제어 상실 ──────────────────────────────────────────────────
  // [Fix-IE08] FM-17(Li-plating 열폭주) 제거 → FM-10(조향각 센서 오류) 교체
  //   근거: FM-17은 TE-03(열폭주) 경로 전용; EPS 토크 이상은 EPS 토크 센서(FM-16)와
  //         조향각 센서 오류(FM-10)가 직접 원인
  //   P(OR) ≈ P(FM-16) + P(FM-10) = 1e-8 + 3e-8 = 4.0e-8
  "IE-08":{ name:"EPS 토크 이상",          nameEn:"EPS Torque Anomaly",
            gate:"OR",  basic_events:["FM-16","FM-10"],                          prob_base:4.0e-8 },
  // [Fix-IE09] FM-12(SOC 추정 오류) 제거 → FM-09(ESC 오작동) 교체
  //   근거: FM-12는 BMS/에너지 경로 전용; ESC·TV 오류의 직접 원인은
  //         ESC 오작동(FM-09), IGBT 단락(FM-13, 인버터 이상→TV 불능), TV 과도 개입(FM-18)
  //   P(OR) ≈ P(FM-09) + P(FM-13) + P(FM-18) = 5e-9 + 5e-9 + 1e-8 = 2.0e-8
  "IE-09":{ name:"ESC·TV 기능 오류",       nameEn:"ESC / Torque Vectoring Fault",
            gate:"OR",  basic_events:["FM-09","FM-13","FM-18"],                  prob_base:2.0e-8 },

  // ── Transfer Gate (IEC 61025 §4.4.5) ──────────────────────────────────────
  // IE-T5: TE-05(조향 제어 상실) 하위 트리를 다른 분석 컨텍스트에서 재사용
  //   transfer_to → buildNode가 해당 TE의 intermediate_causes를 재귀 전개
  //   현재 독립 참조 없음 — 복합 위험 분석(예: UA+조향 동시 상실) 연결 예정
  "IE-T5":{ name:"조향 제어 상실 (전이)",  nameEn:"Steering Loss (Transfer Gate)",
            gate:"TRANSFER", transfer_to:"TE-05",                                prob_base:1.2e-8 },
};